import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../api/client'
import { useAuth } from '../store/auth'
import { useRefresh } from '../store/refresh'
import { useSettings } from '../store/settings'
import {
  clearMediaForHandle,
  countHydration,
  countMediaItems,
  db,
  getMediaAggregates,
  type MediaAggregates,
  listMediaItems,
  listMediaItemsAfter,
  newestRkey,
  type MediaItemRow,
} from '../cache/db'
import { blobUrl, bskyPostUrl, videoPlaylistUrl, videoThumbUrl } from '../utils/handle'
import { hydrateAllForHandle } from '../utils/hydrate'
import { isSensitive } from '../utils/sensitive'
import { formatLocal, formatWeekday, relativeTime } from '../utils/time'
import { HlsVideo } from './HlsVideo'
import { ChevronDown, ChevronUp } from './Icon'

const PAGE_SIZE = 200

type RefreshState = {
  running: boolean
  likesPulled: number
  repostsPulled: number
  postsPulled: number
  likesDone: boolean
  repostsDone: boolean
  postsDone: boolean
  error: string | null
}

export function Gallery({ handle }: { handle: string }) {
  const service = useSettings((s) => s.service)
  const accessJwt = useAuth((s) => s.session?.accessJwt)
  const refreshTrigger = useRefresh((s) => s.counter)
  const [items, setItems] = useState<MediaItemRow[]>([])
  const [totalMedia, setTotalMedia] = useState(0)
  const [totalLikes, setTotalLikes] = useState(0)
  const [hydratedCount, setHydratedCount] = useState(0)
  const [hydrating, setHydrating] = useState(false)
  const [aggregates, setAggregates] = useState<MediaAggregates>({
    total: 0,
    sensitive: 0,
    posts: 0,
    replies: 0,
    reposts: 0,
    likes: 0,
  })
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<MediaItemRow | null>(null)
  const cancelRef = useRef<{ cancelled: boolean; controller: AbortController } | null>(null)
  const reachedEnd = useRef(false)
  const itemsRef = useRef(items)
  itemsRef.current = items
  const [refresh, setRefresh] = useState<RefreshState>({
    running: false,
    likesPulled: 0,
    repostsPulled: 0,
    postsPulled: 0,
    likesDone: false,
    repostsDone: false,
    postsDone: false,
    error: null,
  })
  const refreshRanFor = useRef<string | null>(null)

  const loadHead = useCallback(async () => {
    const fresh = await listMediaItems(handle, 0, PAGE_SIZE)
    setItems(fresh)
    reachedEnd.current = fresh.length < PAGE_SIZE
  }, [handle])

  const loadMore = useCallback(async () => {
    const lastLikedAt = itemsRef.current.length
      ? itemsRef.current[itemsRef.current.length - 1].likedAt
      : null
    const next = await listMediaItemsAfter(handle, lastLikedAt, PAGE_SIZE)
    if (next.length === 0) {
      reachedEnd.current = true
      return
    }
    setItems((prev) => {
      const seen = new Set(prev.map((p) => p.id))
      return [...prev, ...next.filter((n) => !seen.has(n.id))]
    })
  }, [handle])

  const refreshTotals = useCallback(async () => {
    const [m, h, likes, reposts, ownPosts, agg] = await Promise.all([
      countMediaItems(handle),
      countHydration(handle),
      db.records.where('[handle+collection]').equals([handle, 'app.bsky.feed.like']).count(),
      db.records.where('[handle+collection]').equals([handle, 'app.bsky.feed.repost']).count(),
      db.records.where('[handle+collection]').equals([handle, 'app.bsky.feed.post']).count(),
      getMediaAggregates(handle),
    ])
    setTotalMedia(m)
    setHydratedCount(h)
    // Denominator must include EVERY record kind that hydration writes a
    // like_hydration row for — likes, reposts, AND own posts — otherwise
    // hydratedCount can exceed totalLikes and the % overshoots 100.
    setTotalLikes(likes + reposts + ownPosts)
    setAggregates(agg)
  }, [handle])

  useEffect(() => {
    setItems([])
    setError(null)
    reachedEnd.current = false
    refreshTotals()
    loadHead()
  }, [handle, loadHead, refreshTotals])

  // Cancel any in-flight hydration on unmount. Profile.tsx remounts Gallery
  // with key={handle}, so this fires whenever the user switches profiles —
  // otherwise a paused/running scan for handle A would keep walking the
  // AppView and writing for A long after the user moved to handle B.
  // We both flip the cancelled flag (stops loops) and abort the controller
  // (kills in-flight fetches immediately, no wait for current page to finish).
  useEffect(() => {
    return () => {
      if (cancelRef.current) {
        cancelRef.current.cancelled = true
        cancelRef.current.controller.abort()
      }
    }
  }, [])

  const startHydration = useCallback(async () => {
    if (hydrating) return
    cancelRef.current = { cancelled: false, controller: new AbortController() }
    const ab = cancelRef.current
    setHydrating(true)
    setError(null)
    try {
      await hydrateAllForHandle(handle, {
        signal: ab.controller.signal,
        onProgress: async () => {
          if (ab.cancelled) return
          // Cheap incremental refresh — pulls aggregate counts + head page
          // so the user sees numbers ticking up as labels get tagged.
          await refreshTotals()
          await loadHead()
        },
      })
      if (ab.cancelled) return
      await refreshTotals()
      await loadHead()
    } catch (err) {
      if (ab.cancelled) return
      if (err instanceof DOMException && err.name === 'AbortError') return
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      if (!ab.cancelled) setHydrating(false)
    }
  }, [handle, hydrating, loadHead, refreshTotals])

  const cancelHydration = useCallback(() => {
    if (cancelRef.current) {
      cancelRef.current.cancelled = true
      cancelRef.current.controller.abort()
    }
    setHydrating(false)
  }, [])

  const resetMedia = useCallback(async () => {
    if (
      !confirm(
        'Wipe extracted media for this handle? Hydration will restart from scratch and re-process every cached like / repost / post (records themselves stay cached).',
      )
    )
      return
    await clearMediaForHandle(handle)
    setItems([])
    setTotalMedia(0)
    setHydratedCount(0)
    reachedEnd.current = false
    // Force the auto-hydration effect to re-fire by invalidating refresh ref,
    // then kick the hydration directly so the user doesn't wait for the next
    // render tick.
    refreshRanFor.current = null
    setTimeout(() => startHydration(), 0)
  }, [handle, startHydration])

  // On entering the gallery for a handle: pull anything newer than what's
  // already cached for likes + reposts + posts in parallel, then let the
  // hydration effect below pick up the new items. Re-fires when the global
  // refresh button is pressed.
  useEffect(() => {
    if (refreshRanFor.current === `${handle}|${refreshTrigger}`) return
    refreshRanFor.current = `${handle}|${refreshTrigger}`
    let cancelled = false
    const controller = new AbortController()

    async function walk(
      collection: 'app.bsky.feed.like' | 'app.bsky.feed.repost' | 'app.bsky.feed.post',
      onProgress: (n: number) => void,
    ): Promise<number> {
      const stop = (await newestRkey(handle, collection)) ?? undefined
      let cursor: string | undefined
      let total = 0
      let pages = 0
      while (!cancelled) {
        let attempt = 0
        let page: Awaited<ReturnType<typeof api.page>> | null = null
        while (attempt < 4 && !cancelled) {
          try {
            page = await api.page({
              handle,
              collection,
              cursor,
              limit: pages === 0 ? 50 : 100,
              stopAtRkey: stop,
              service,
              accessJwt,
              signal: controller.signal,
            })
            break
          } catch (err) {
            if (cancelled || (err instanceof DOMException && err.name === 'AbortError')) return total
            attempt++
            await new Promise((r) => setTimeout(r, 600 * 2 ** attempt))
          }
        }
        if (!page) break
        total += page.records.length
        pages += 1
        if (!cancelled) onProgress(total)
        cursor = page.cursor ?? undefined
        if (page.reachedKnown || !cursor) break
      }
      return total
    }

    setRefresh({
      running: true,
      likesPulled: 0,
      repostsPulled: 0,
      postsPulled: 0,
      likesDone: false,
      repostsDone: false,
      postsDone: false,
      error: null,
    })
    Promise.all([
      walk('app.bsky.feed.like', (n) =>
        setRefresh((s) => ({ ...s, likesPulled: n })),
      ).then((n) => {
        if (!cancelled) setRefresh((s) => ({ ...s, likesPulled: n, likesDone: true }))
      }),
      walk('app.bsky.feed.repost', (n) =>
        setRefresh((s) => ({ ...s, repostsPulled: n })),
      ).then((n) => {
        if (!cancelled) setRefresh((s) => ({ ...s, repostsPulled: n, repostsDone: true }))
      }),
      walk('app.bsky.feed.post', (n) =>
        setRefresh((s) => ({ ...s, postsPulled: n })),
      ).then((n) => {
        if (!cancelled) setRefresh((s) => ({ ...s, postsPulled: n, postsDone: true }))
      }),
    ])
      .catch((err) => {
        if (!cancelled)
          setRefresh((s) => ({ ...s, error: err instanceof Error ? err.message : String(err) }))
      })
      .finally(() => {
        if (!cancelled) {
          setRefresh((s) => ({ ...s, running: false }))
          // Re-count totals AND reload the head page so any newly-pulled
          // records that were already-hydrated (e.g. a recent post extracted
          // inline) appear immediately. Without loadHead the user has to
          // hit "reset" to see fresh content on a tab switch.
          refreshTotals()
          loadHead()
        }
      })

    return () => {
      cancelled = true
      controller.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handle, service, accessJwt, refreshTrigger])

  // Auto-start hydration once the refresh round above is done (or had nothing
  // new). We re-evaluate every time totals change.
  useEffect(() => {
    if (refresh.running) return
    if (totalLikes === 0) return
    if (hydratedCount >= totalLikes) return
    if (hydrating) return
    startHydration()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh.running, totalLikes, hydratedCount])

  // Clamp at 100 defensively — if the denominator drifts (e.g. records get
  // re-counted mid-flight), at least the user never sees a 142% scanned.
  const pct = totalLikes > 0 ? Math.min(100, Math.round((hydratedCount / totalLikes) * 100)) : 100
  // Dedup by IMAGE identity (did + rkey + cid), not by verb — otherwise the
  // same image both liked and reposted shows up twice. Items arrive
  // already sorted by likedAt desc so the kept entry is the most recent.
  const dedupedItems = useMemo(() => {
    const seen = new Set<string>()
    const out: MediaItemRow[] = []
    for (const it of items) {
      const key = `${it.did}|${it.rkey}|${it.cid}`
      if (seen.has(key)) continue
      seen.add(key)
      out.push(it)
    }
    return out
  }, [items])

  const refreshPct = (() => {
    if (!refresh.running && refresh.likesDone && refresh.repostsDone) return 100
    if (refresh.likesDone && refresh.repostsDone) return 100
    return refresh.likesDone && refresh.repostsDone ? 100 : refresh.likesDone || refresh.repostsDone ? 50 : 5
  })()

  type NsfwFilter = 'all' | 'sensitive' | 'safe'
  type SourceFilter = 'all' | 'posts' | 'replies' | 'reposts' | 'likes'
  const [nsfwFilter, setNsfwFilter] = useState<NsfwFilter>('all')
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all')
  const [statsCollapsed, setStatsCollapsed] = useState(false)
  const filteredItems = useMemo(() => {
    return dedupedItems.filter((it) => {
      // NSFW filter
      if (nsfwFilter === 'sensitive' && !isSensitive(it.labels)) return false
      if (nsfwFilter === 'safe' && isSensitive(it.labels)) return false
      // Source filter
      if (sourceFilter === 'posts' && !(it.verb === 'posted' && !it.isReply)) return false
      if (sourceFilter === 'replies' && !(it.verb === 'posted' && it.isReply)) return false
      if (sourceFilter === 'reposts' && it.verb !== 'reposted') return false
      if (sourceFilter === 'likes' && it.verb !== 'liked') return false
      return true
    })
  }, [dedupedItems, nsfwFilter, sourceFilter])
  // Counts come from the full DB aggregate (db.media_items scan), not the
  // currently-loaded scroll chunk — otherwise filters and badges would only
  // reflect ~200 items and the user would have to scroll the whole 80k+ list
  // to see the real totals.
  const sensitiveCount = aggregates.sensitive
  const sourceCounts = {
    posts: aggregates.posts,
    replies: aggregates.replies,
    reposts: aggregates.reposts,
    likes: aggregates.likes,
  }
  const totalForFilter = aggregates.total

  return (
    <div className="mx-auto mt-4 max-w-6xl px-4 pb-16">
      {refresh.running || refresh.likesPulled + refresh.repostsPulled > 0 ? (
        <div className="glass-tinted mb-3 px-4 py-3 text-xs">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div>
              <strong className="text-white">
                {refresh.running ? 'Refreshing likes + reposts…' : 'Refresh complete'}
              </strong>{' '}
              <span className="text-ink-300">
                pulled {refresh.likesPulled.toLocaleString()} new likes,{' '}
                {refresh.repostsPulled.toLocaleString()} new reposts since last visit
              </span>
            </div>
            {refresh.error ? (
              <span className="text-rose-200" title={refresh.error}>
                {refresh.error}
              </span>
            ) : null}
          </div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/5">
            <div
              className="h-1.5 rounded-full transition-all"
              style={{ width: `${refreshPct}%`, background: 'var(--accent-strong)' }}
            />
          </div>
          <div className="mt-1 grid grid-cols-2 gap-2 text-[11px] text-ink-300">
            <div className="flex items-center gap-1">
              <span className={refresh.likesDone ? 'text-emerald-300' : 'text-ink-300'}>
                {refresh.likesDone ? '✓' : '…'}
              </span>
              likes: +{refresh.likesPulled.toLocaleString()}
            </div>
            <div className="flex items-center gap-1">
              <span className={refresh.repostsDone ? 'text-emerald-300' : 'text-ink-300'}>
                {refresh.repostsDone ? '✓' : '…'}
              </span>
              reposts: +{refresh.repostsPulled.toLocaleString()}
            </div>
          </div>
        </div>
      ) : null}

      <section className="glass-tinted mb-3 overflow-hidden">
        <button
          type="button"
          onClick={() => setStatsCollapsed((v) => !v)}
          className="flex w-full items-center justify-between gap-2 px-4 py-2 text-[11px] uppercase tracking-widest text-ink-300 transition hover:bg-white/5"
          aria-expanded={!statsCollapsed}
        >
          <span className="flex items-center gap-2">
            <span>gallery controls</span>
            <span className="rounded-full border border-white/10 bg-white/5 px-1.5 py-0.5 text-[9px] text-ink-200">
              {totalMedia.toLocaleString()}
            </span>
            {hydrating ? <span className="accent-text">scanning…</span> : null}
          </span>
          {statsCollapsed ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
        </button>

        {statsCollapsed ? null : (
          <div className="space-y-3 px-4 pb-3 text-xs text-ink-300">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex flex-1 flex-wrap items-baseline gap-x-3 gap-y-1">
                <span>
                  <strong className="text-base text-white">{totalMedia.toLocaleString()}</strong>{' '}
                  media
                </span>
                <span className="text-ink-400">•</span>
                <span>
                  {hydratedCount.toLocaleString()} / {totalLikes.toLocaleString()} scanned ({pct}%)
                </span>
                <span className="text-ink-400">•</span>
                <span>
                  showing <strong className="text-white">{filteredItems.length.toLocaleString()}</strong>{' '}
                  ({sensitiveCount.toLocaleString()} sensitive)
                </span>
              </div>
              <div className="flex items-center gap-2">
                {hydrating ? (
                  <button type="button" className="glass-button text-xs" onClick={cancelHydration}>
                    pause
                  </button>
                ) : hydratedCount < totalLikes ? (
                  <button
                    type="button"
                    className="glass-button glass-button--accent text-xs"
                    onClick={startHydration}
                  >
                    resume scan
                  </button>
                ) : null}
                <button
                  type="button"
                  className="glass-button text-xs"
                  onClick={resetMedia}
                  title="wipe extracted media + hydration log"
                >
                  reset gallery
                </button>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-1 text-[11px]">
              <span className="mr-1 uppercase tracking-widest text-ink-400">source:</span>
              {([
                { k: 'all', n: totalForFilter, label: 'all' },
                { k: 'posts', n: sourceCounts.posts, label: 'posts' },
                { k: 'replies', n: sourceCounts.replies, label: 'replies' },
                { k: 'reposts', n: sourceCounts.reposts, label: 'reposts' },
                { k: 'likes', n: sourceCounts.likes, label: 'likes' },
              ] as { k: SourceFilter; n: number; label: string }[]).map((opt) => (
                <button
                  key={opt.k}
                  type="button"
                  onClick={() => setSourceFilter(opt.k)}
                  className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 transition ${
                    sourceFilter === opt.k
                      ? 'border-[color:var(--accent-strong)] bg-white/10 text-white'
                      : 'border-white/10 bg-white/5 text-ink-200 hover:bg-white/10'
                  }`}
                >
                  {opt.label}
                  <span className="rounded-full bg-white/10 px-1.5 py-0.5 text-[9px] text-ink-200">
                    {opt.n.toLocaleString()}
                  </span>
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-1 text-[11px]">
              <span className="mr-1 uppercase tracking-widest text-ink-400">sensitive:</span>
              {(['all', 'safe', 'sensitive'] as NsfwFilter[]).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setNsfwFilter(f)}
                  className={`rounded-full border px-3 py-1 transition ${
                    nsfwFilter === f
                      ? 'border-[color:var(--accent-strong)] bg-white/10 text-white'
                      : 'border-white/10 bg-white/5 text-ink-200 hover:bg-white/10'
                  }`}
                  title={
                    f === 'all'
                      ? 'show every cached image / video'
                      : f === 'safe'
                        ? 'hide images on posts labeled porn / sexual / nudity / sexual-figurative / graphic-media / gore'
                        : 'show only images on posts with one of those labels'
                  }
                >
                  {f === 'all'
                    ? 'all'
                    : f === 'safe'
                      ? 'safe (hide sensitive)'
                      : `sensitive only (${sensitiveCount})`}
                </button>
              ))}
              <span className="ml-2 text-[10px] text-ink-400">
                labels read from each source post's bsky moderation labels
              </span>
            </div>
          </div>
        )}
      </section>

      {totalLikes === 0 ? (
        <div className="glass px-4 py-10 text-center text-ink-300">
          Nothing cached yet. Open the <strong>likes</strong> and/or <strong>reposts</strong>{' '}
          tab and click "Load entire feed" first; the gallery extracts media from both in the
          background.
        </div>
      ) : null}

      {error ? (
        <div className="glass border-rose-400/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
          {error}
        </div>
      ) : null}

      <VirtualThumbGrid
        items={filteredItems}
        onSelect={setSelected}
        onNearEnd={loadMore}
      />

      <div className="mt-6 flex justify-center text-xs text-ink-400">
        {!reachedEnd.current && items.length < totalMedia
          ? `${items.length.toLocaleString()} / ${totalMedia.toLocaleString()} loaded — keep scrolling`
          : null}
      </div>

      {selected ? <Lightbox item={selected} onClose={() => setSelected(null)} /> : null}
    </div>
  )
}

function colsFor(width: number): number {
  if (width >= 1024) return 5
  if (width >= 768) return 4
  if (width >= 640) return 3
  return 2
}

const GRID_GAP = 12 // px (gap-3)
const ROW_BUFFER = 2 // rows of buffer above + below visible window

type TileProps = {
  item: MediaItemRow
  size: number
  top: number
  left: number
  onSelect: (i: MediaItemRow) => void
}
const Tile = (function () {
  function T({ item, size, top, left, onSelect }: TileProps) {
    return (
      <button
        type="button"
        onClick={() => onSelect(item)}
        style={{ position: 'absolute', top, left, width: size, height: size }}
        className="group overflow-hidden rounded-xl border border-white/10 bg-ink-900/50 transition hover:border-[color:var(--accent-strong)]"
      >
        {item.isVideo ? (
          <>
            <img
              src={videoThumbUrl(item.did, item.cid)}
              alt=""
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover transition group-hover:scale-105"
            />
            <div className="pointer-events-none absolute inset-0 grid place-items-center bg-black/30 text-3xl text-white/90">
              ▶
            </div>
          </>
        ) : (
          <img
            src={blobUrl(item.did, item.cid, 'feed_thumbnail')}
            alt={item.alt ?? ''}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition group-hover:scale-105"
          />
        )}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-2 py-1 text-left text-[10px] text-white opacity-0 transition group-hover:opacity-100">
          {item.verb === 'reposted' ? 'reposted' : 'liked'}{' '}
          {formatWeekday(new Date(item.likedAt))} • {relativeTime(new Date(item.likedAt))}
        </div>
        {item.verb === 'reposted' ? (
          <div
            className="pointer-events-none absolute left-1.5 top-1.5 rounded-md border border-white/20 bg-black/60 px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-white"
            title="from a repost"
          >
            ⇄
          </div>
        ) : null}
        {isSensitive(item.labels) ? (
          <div
            className="pointer-events-none absolute right-1.5 top-1.5 rounded-md border border-rose-400/40 bg-rose-500/30 px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-rose-100"
            title={`labels: ${(item.labels ?? []).join(', ')}`}
          >
            !
          </div>
        ) : null}
      </button>
    )
  }
  return memo(T, (a, b) =>
    a.item.id === b.item.id &&
    a.size === b.size &&
    a.top === b.top &&
    a.left === b.left,
  )
})()

function VirtualThumbGrid({
  items,
  onSelect,
  onNearEnd,
}: {
  items: MediaItemRow[]
  onSelect: (i: MediaItemRow) => void
  onNearEnd: () => void
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)
  const [scrollState, setScrollState] = useState({ first: 0, last: 50 })
  const onNearEndRef = useRef(onNearEnd)
  onNearEndRef.current = onNearEnd

  // Measure container width
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const ro = new ResizeObserver(() => setWidth(el.clientWidth))
    ro.observe(el)
    setWidth(el.clientWidth)
    return () => ro.disconnect()
  }, [])

  const cols = width > 0 ? colsFor(width) : 2
  const itemSize =
    width > 0 ? Math.floor((width - (cols - 1) * GRID_GAP) / cols) : 0
  const rowHeight = itemSize + GRID_GAP
  const rowCount = Math.ceil(items.length / cols)
  const totalHeight = Math.max(0, rowCount * rowHeight - GRID_GAP)

  // Visible window: based on window scroll relative to container top
  useEffect(() => {
    if (rowHeight <= 0) return
    let raf = 0
    const update = () => {
      const el = containerRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const winH = window.innerHeight
      const visibleTop = Math.max(0, -rect.top)
      const visibleBottom = Math.max(0, winH - rect.top)
      const firstRow = Math.max(0, Math.floor(visibleTop / rowHeight) - ROW_BUFFER)
      const lastRow = Math.min(
        rowCount,
        Math.ceil(visibleBottom / rowHeight) + ROW_BUFFER,
      )
      const first = firstRow * cols
      const last = Math.min(items.length, lastRow * cols)
      setScrollState((s) => (s.first === first && s.last === last ? s : { first, last }))
      // Trigger onNearEnd when within 1000px of the bottom
      const distFromBottom = totalHeight - visibleBottom
      if (distFromBottom < 1000) onNearEndRef.current()
    }
    const onScroll = () => {
      if (raf) return
      raf = requestAnimationFrame(() => {
        raf = 0
        update()
      })
    }
    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', update)
    return () => {
      if (raf) cancelAnimationFrame(raf)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', update)
    }
  }, [rowHeight, rowCount, cols, items.length, totalHeight])

  const visible: MediaItemRow[] = []
  for (let i = scrollState.first; i < scrollState.last; i++) {
    if (items[i]) visible.push(items[i])
  }

  return (
    <div ref={containerRef} style={{ position: 'relative', height: totalHeight }}>
      {visible.map((item, k) => {
        const idx = scrollState.first + k
        const row = Math.floor(idx / cols)
        const col = idx % cols
        const top = row * rowHeight
        const left = col * (itemSize + GRID_GAP)
        return (
          <Tile
            key={item.id}
            item={item}
            size={itemSize}
            top={top}
            left={left}
            onSelect={onSelect}
          />
        )
      })}
    </div>
  )
}

function Lightbox({ item, onClose }: { item: MediaItemRow; onClose: () => void }) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const postUrl = bskyPostUrl(item.did, item.rkey)
  const likedDate = new Date(item.likedAt)

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/80 p-4 backdrop-blur-md"
      onClick={onClose}
    >
      <div
        className="relative flex max-h-full w-full max-w-5xl flex-col items-center gap-3"
        onClick={(e) => e.stopPropagation()}
      >
        {item.isVideo ? (
          <div className="w-full max-w-3xl overflow-hidden rounded-2xl border border-white/10 bg-black">
            <HlsVideo
              controls
              autoPlay
              playsInline
              poster={videoThumbUrl(item.did, item.cid)}
              className="aspect-video w-full bg-black"
              src={videoPlaylistUrl(item.did, item.cid)}
            />
          </div>
        ) : (
          <img
            src={blobUrl(item.did, item.cid, 'feed_fullsize')}
            alt={item.alt ?? ''}
            className="max-h-[78vh] w-auto rounded-2xl object-contain shadow-glass"
          />
        )}
        <div className="glass flex w-full max-w-3xl items-center justify-between gap-3 px-4 py-2 text-sm">
          <span title={formatLocal(likedDate)}>
            <span className="accent-text">{item.verb === 'reposted' ? 'reposted' : 'liked'}</span>{' '}
            {formatWeekday(likedDate)}, {relativeTime(likedDate)}{' '}
            <span className="text-ink-300">({formatLocal(likedDate)})</span>
          </span>
          <div className="flex items-center gap-2">
            <a
              className="glass-button glass-button--accent text-xs"
              href={postUrl}
              target="_blank"
              rel="noreferrer"
              title="open the original Bluesky post"
            >
              ↗ open post
            </a>
            <button type="button" className="glass-button text-xs" onClick={onClose}>
              close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
